import { createAsyncThunk } from '@reduxjs/toolkit';
import employeeTasks from '../../../api/employeeTasksApi';
import type { CigarProductionPlanTaskStatus } from '../../../constants/enums';

// ---------------------------------------------------------------------

const REDUCER_NAME = 'employeeTasks';

export const GetEmployeeTasks = createAsyncThunk(
  `${REDUCER_NAME}/getEmployeeTasks`,
  async (params: any | undefined, thunkAPI) => {
    const res = await employeeTasks.get(params);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetTechnicalRawEmployeeTasks = createAsyncThunk(
  `${REDUCER_NAME}/getTechnicalRawEmployeeTasks`,
  async (_, thunkAPI) => {
    const res = await employeeTasks.getTechnicalRaw();

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const UpdateCigarProductionPlanTaskStatus = createAsyncThunk(
  `${REDUCER_NAME}/updateCigarProductionPlanTaskStatus`,
  async ({ id, status, notes }: { id: string; status: CigarProductionPlanTaskStatus; notes?: string }, thunkAPI) => {
    const payload: { status: CigarProductionPlanTaskStatus; notes?: string } = { status };

    if (notes && notes.trim().length > 0) {
      payload.notes = notes.trim();
    }

    const res = await employeeTasks.updateStatus(id, payload);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Update failed' });
  }
);

export const UpdateEmployeeTask = createAsyncThunk(
  `${REDUCER_NAME}/updateEmployeeTask`,
  async (
    {
      id,
      assignedUser,
      title,
      description,
      dueDate,
    }: { id: string; assignedUser: string; title: string; description?: string; dueDate?: string },
    thunkAPI
  ) => {
    const payload: { assignedUser: string; title: string; description?: string; dueDate?: string } = {
      assignedUser,
      title,
    };

    if (description && description.trim().length > 0) {
      payload.description = description.trim();
    }

    if (dueDate) {
      payload.dueDate = dueDate;
    }

    const res = await employeeTasks.updateSimple(id, payload);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Update failed' });
  }
);

export const CreateEmployeeTask = createAsyncThunk(
  `${REDUCER_NAME}/createEmployeeTask`,
  async (
    {
      assignedUser,
      title,
      description,
      dueDate,
    }: { assignedUser: string; title: string; description?: string; dueDate?: string },
    thunkAPI
  ) => {
    const payload: { assignedUser: string; title: string; description?: string; dueDate?: string } = {
      assignedUser,
      title,
    };

    if (description && description.trim().length > 0) {
      payload.description = description.trim();
    }

    if (dueDate) {
      payload.dueDate = dueDate;
    }

    const res = await employeeTasks.createSimple(payload);

    if (res.status === 201 || res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Creation failed' });
  }
);

export const CreateCycleCountForCigarProductionPlanTask = createAsyncThunk(
  `${REDUCER_NAME}/createCycleCountForCigarProductionPlanTask`,
  async (
    { id, assignedUser, rooms, dueDate }: { id: string; assignedUser?: string; rooms?: string[]; dueDate?: string },
    thunkAPI
  ) => {
    const payload: { assignedUser?: string; rooms?: string[]; dueDate?: string } = {};

    if (assignedUser) {
      payload.assignedUser = assignedUser;
    }

    if (rooms && rooms.length > 0) {
      payload.rooms = rooms;
    }

    if (dueDate) {
      payload.dueDate = dueDate;
    }

    const res = await employeeTasks.createCycleCount(id, Object.keys(payload).length > 0 ? payload : undefined);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Creation failed' });
  }
);

export const CreateCigarProductionScheduleTask = createAsyncThunk(
  `${REDUCER_NAME}/createCigarProductionScheduleTask`,
  async ({ id, assignedUser, dueDate }: { id: string; assignedUser?: string; dueDate?: string }, thunkAPI) => {
    const payload: { assignedUser?: string; dueDate?: string } = {};

    if (assignedUser) {
      payload.assignedUser = assignedUser;
    }

    if (dueDate) {
      payload.dueDate = dueDate;
    }

    const res = await employeeTasks.createCigarProductionScheduleTask(
      id,
      Object.keys(payload).length > 0 ? payload : undefined
    );

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Creation failed' });
  }
);

export const CreateTobaccoProductionPlanningTask = createAsyncThunk(
  `${REDUCER_NAME}/createTobaccoProductionPlanningTask`,
  async ({ id, assignedUser, dueDate }: { id: string; assignedUser?: string; dueDate?: string }, thunkAPI) => {
    const payload: { assignedUser?: string; dueDate?: string } = {};

    if (assignedUser) {
      payload.assignedUser = assignedUser;
    }

    if (dueDate) {
      payload.dueDate = dueDate;
    }

    const res = await employeeTasks.createTobaccoProductionPlanningTask(
      id,
      Object.keys(payload).length > 0 ? payload : undefined
    );

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Creation failed' });
  }
);

export const ArchiveCigarProductionPlanTask = createAsyncThunk(
  `${REDUCER_NAME}/archiveCigarProductionPlanTask`,
  async ({ id }: { id: string }, thunkAPI) => {
    const res = await employeeTasks.archive(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Update failed' });
  }
);

export const DeleteCigarProductionPlanTask = createAsyncThunk(
  `${REDUCER_NAME}/deleteCigarProductionPlanTask`,
  async ({ id }: { id: string }, thunkAPI) => {
    const res = await employeeTasks.remove(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Delete failed' });
  }
);

export const GetEmployeeTaskHistory = createAsyncThunk(
  `${REDUCER_NAME}/getEmployeeTaskHistory`,
  async ({ id }: { id: string }, thunkAPI) => {
    const res = await employeeTasks.getHistory(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);
